import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useStore } from 'effector-react';
import { closeModal } from '@stores/modals';
import { ALL_LOCALE, LOCALE_NAME } from '@stores/localeWeight';
import { Button, DatePicker, Form, Input, Modal, Radio, Select, Spin } from 'antd';
import { ALL_TEXT_KEYS, TEXT_KEYS_NAME } from '@constants/textKeys';
import { IPromotionActivity, IPromotionActivityAction } from '@interfaces/IPromotionActivity';
import useTranslate from '@hooks/useTranslate';
import postSelector, { getPostById, reset } from '@stores/postSelector';
import { useForm } from 'antd/lib/form/Form';
import { createPromotionActivity, editPromotionActivity } from '@stores/activity';
import componentContent from '../PromotionActivityPage.content.json';
import { FULL_TIME_FORMAT, TIME_FORMAT } from '@config/format';
import DebounceSelect from '@fields/DebounceSelect/DebounceSelect';
import {
  fetchGameList,
  mapValueToRequest,
  mapDataToFieldsValue,
  rulesValidateForm,
} from './PromotionActivityEditModal.helpers';
import { PromotionActivityFormValue } from './PromotionActivityEditModal.types';
import FeaturingPostPreview from '@pages/FeaturingPage/FeaturingPostPreview/FeaturingPostPreview';
import css from './PromotionActivityEditModal.module.less';
import { Option } from 'antd/lib/mentions';
import ExclamationCircleFilled from '@ant-design/icons/lib/icons/ExclamationCircleFilled';

const { confirm } = Modal;

const LOCALES = ALL_LOCALE.map((locale) => ({
  label: LOCALE_NAME[locale],
  value: locale,
}));

const KEYS = ALL_TEXT_KEYS.map((textKey) => ({
  label: TEXT_KEYS_NAME[textKey],
  value: textKey,
}));

const PromotionActivityEditModal: FunctionComponent<{ data?: IPromotionActivity }> = ({ data }) => {
  // for change color of Save button
  const [isHover, setIsHover] = useState(false);

  const {
    post,
    preloader: { getPostById: preloaderPost },
  } = useStore(postSelector);

  const handleMouseEnter = () => {
    setIsHover(true);
  };

  const handleMouseLeave = () => {
    setIsHover(false);
  };
  // for change color of Save button
  const content = useTranslate(componentContent);
  const [form] = useForm();
  const locales = useMemo(() => LOCALES, []);

  useEffect(() => {
    if (data) {
      const fieldsValue = mapDataToFieldsValue(data);
      form.setFieldsValue(fieldsValue);
      getPostById(fieldsValue.postId);
    } else {
      form.resetFields();
      reset();
    }
  }, [data, form]);

  const onFinish = (values: PromotionActivityFormValue) => {
    const dataForRequest: IPromotionActivityAction = mapValueToRequest(values);
    if (data?.id) {
      editPromotionActivity({
        ...dataForRequest,
        id: data.id,
      });
    } else {
      createPromotionActivity(dataForRequest);
    }
    closeModal({ name: 'activity' });
  };

  const onClose = (
    description: string,
    title: string,
    no: string,
    yes: string,
    onConfirm?: any,
  ) => {
    confirm({
      title: title,
      icon: <ExclamationCircleFilled />,
      content: description,
      okText: yes,
      cancelText: no,
      onOk() {
        onConfirm();
      },
      onCancel() {
        closeModal({ name: 'activity' });
      },
    });
  };
  return (
    <Modal
      title={data ? content.edit_notification : content.add_notification}
      visible
      onCancel={() =>
        onClose(
          content.confirm_description,
          content.confirm_title,
          content.no,
          content.yes,
          form.submit,
        )
      }
      width={800}
      footer={[
        <Button
          key='cancel'
          type='primary'
          danger
          onClick={() =>
            onClose(
              content.confirm_description,
              content.confirm_title,
              content.no,
              content.yes,
              form.submit,
            )
          }
        >
          {content.cancel}
        </Button>,
        <Button
          key='submit'
          htmlType='submit'
          type='primary'
          className={css.submitButton}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={() =>
            onClose(
              content.confirm_description,
              content.confirm_title,
              content.no,
              content.yes,
              form.submit,
            )
          }
        >
          {content.save}
        </Button>,
      ]}
    >
      <Form
        onFinish={onFinish}
        form={form}
        labelAlign='left'
        labelWrap
        labelCol={{ flex: '150px' }}
      >
        <Spin spinning={false}>
          <Form.Item
            label={content.notification_name}
            name='name'
            validateTrigger='onBlur'
            rules={[rulesValidateForm.name.base]}
          >
            <Input type={'text'} />
          </Form.Item>

          <Form.Item
            label={content.post_id}
            name='postId'
            validateTrigger='onBlur'
            rules={[
              rulesValidateForm.postId.base,
              {
                validator: (rule, value) =>
                  rulesValidateForm.postId.validator(rule, value, content),
              },
            ]}
          >
            <Input
              type={'number'}
              min={1}
            />
          </Form.Item>
          {
            <div className={css.postContainer}>
              <Spin spinning={!!preloaderPost}>
                {post.data ? <FeaturingPostPreview post={post.data} /> : null}
              </Spin>
            </div>
          }
          <Form.Item
            label={content.display_period}
            name='date'
            rules={[rulesValidateForm.date.base]}
          >
            <DatePicker.RangePicker
              showTime={{ format: TIME_FORMAT }}
              format={FULL_TIME_FORMAT}
            />
          </Form.Item>

          <Form.Item
            label={content.notification_text}
            name='textL18nKey'
          >
            <Select
              style={{ width: 240 }}
              defaultValue={content.choose_key}
            >
              {KEYS &&
                Object.entries(KEYS).map(([textL18nKey, { value, label }]) => {
                  return (
                    <Option
                      value={value}
                      key={textL18nKey}
                    >
                      {label}
                    </Option>
                  );
                })}
            </Select>
          </Form.Item>

          <Form.Item
            label={content.locales}
            name='locale'
          >
            <Radio.Group>
              {locales.map((locale) => (
                <Radio
                  value={locale.value}
                  key={locale.value}
                >
                  {locale.label}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={content.games}
            name='gameIds'
          >
            <DebounceSelect
              mode='multiple'
              placeholder={content.select_games}
              fetchOptions={fetchGameList}
            />
          </Form.Item>
        </Spin>
      </Form>
    </Modal>
  );
};

export default PromotionActivityEditModal;
